/**
 * What each placeholder is called, in the language the authors write in.
 *
 * The NAMES are part of the contract. `{{clinic_name}}` is what the template
 * engine resolves, what `prompts.variables` declares, and what the lint checks;
 * changing any of that to Japanese would mean a rename across the server, the
 * database and every stored prompt, for a gain that is entirely presentational.
 *
 * So the name stays English and the LABEL is Japanese. The editor shows
 * 「クリニック名」 and stores `{{clinic_name}}`; nothing behind the screen knows
 * the difference.
 *
 * An unlabelled name falls back to itself rather than throwing. The catalogue
 * lives on the server (modules/ai/variables.ts) and reaches the client through
 * the prompt's own `variables` list, so a name added there before a label is
 * added here should appear as itself - visibly missing a translation, rather
 * than missing from the list.
 */
const LABELS: Record<string, string> = {
  // Who and where
  clinic_name: 'クリニック名',
  store_name: '店舗名',
  prefecture: '都道府県',
  city: '市区町村',
  clinic_url: 'クリニックURL',
  reservation_url: '予約URL',
  access: 'アクセス',
  hours: '診療時間',
  supervisor: '監修者',

  // The article being written
  article_title: '記事タイトル',
  heading: '見出し',
  marker: '節の目印',
  keyword: '対策キーワード',
  required_keywords: '必須キーワード',
  target_length: '目標文字数',
  tone: '文体・トーン',
  user_additional_prompt: '追加の指示',

  // Who it is written for
  persona: 'ペルソナ',
  reader_concerns: '読者の悩み',

  // What the clinic offers
  treatments: '施術・コース・料金',
  treatment_price: '施術料金',
  strengths: '強み',
  equipment: '設備',

  // Images
  title: 'タイトル',
  scene_line: 'シーン説明',
  slot_hint: '配置の指定',
  composition_hint: '構図の指定',
};

/** The Japanese label for a placeholder, or the name itself when there is none. */
export const variableLabel = (name: string): string => LABELS[name] ?? name;

/** Whether this name has a translation. Used to flag one that has outrun this file. */
export const isLabelled = (name: string): boolean => name in LABELS;
